import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import styled, { keyframes, css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { AppMode, LanguageCode } from '../types/types';
import { useTheme } from './ThemeContext';
import DailyStreakAnimation from './DailyStreakAnimation';
import StreakAnimation from './StreakAnimation';
import { culturalAnecdotes, getTodayPhrase } from '../data/mascotContent';
import { readDailyStreak } from '../hooks/useDailyStreak';
import { EmojiText } from './common/Picto';

interface MascotProps {
  onClick?: () => void;
  onHelp?: () => void;
  currentCategory: AppMode;
  streak?: number;
  lastAnswerCorrect?: boolean | null;
  languageCode?: LanguageCode;
  userName?: any;
  /** Nombre total de mots uniques appris (userProgress.learnedWords.size) */
  wordsLearned?: number;
  /** Nombre de mots appris dans la session en cours (sessionLearnedWords.size) */
  sessionWordsLearned?: number;
}

// ===================================================================
// Messages
// ===================================================================

type EncouragementPool = {
  correct: string[];
  wrong: string[];
  streak: string[];
  comeback: string[];
};

const languageEncouragement: Record<LanguageCode, EncouragementPool> = {
  'wf': {
    correct: [
      "Waaw{name} ! Exactement — tu parles déjà wolof ! ✨",
      "Dafa rafet{name} ! C'est parfait ! 🌺",
      "Tu as le génie du wolof{name} ! Les griots seraient fiers ! 🎵",
      "La Teranga, c'est aussi partager la connaissance{name} — et tu y arrives ! 🤝",
      "Tu maîtrises ça comme un Dakarois{name} ! 🦁",
      "Le Lion du Sénégal approuve{name} ! 🦁",
      "Parfait{name} ! Le baobab pousse lentement, mais solidement — comme toi ! 🌳",
      "Excellent{name} ! Même les Lébous de Dakar applaudissent ! ☀️",
    ],
    wrong: [
      "Bul sañ{name} ! Ne te décourage pas, le wolof prend du temps ! 💪",
      "Pas grave{name} ! Même les Dakarois ont dû apprendre un jour !",
      "Les griots disent : chaque erreur est une leçon{name} ! 🎵",
      "Courage{name} ! La Teranga, c'est aussi accueillir ses erreurs ! 🌺",
      "Presque{name} ! Encore un essai et tu auras la Teranga de ce mot ! 🤝",
      "Continue{name} ! Le Sahel ne se traverse pas en un seul pas 🌾",
    ],
    streak: [
      "Quelle série{name} ! Les griots chanteraient pour toi ce soir ! 🎵🔥",
      "{name}, tu es en feu ! Même Dakar s'en souviendra ! 🔥🦁",
      "Le wolof n'a plus de secrets pour toi{name} ! ✨",
      "Impressionnant{name} ! C'est ça, la Teranga du savoir ! 🌟",
    ],
    comeback: [
      "Le baobab résiste toujours{name} ! 🌳",
      "Belle reprise{name} ! Le Lion du Sénégal est de retour ! 🦁🚀",
      "{name}, tu rebondis comme Dakar après la pluie ! ☀️",
    ],
  },
  'ba': {
    correct: [
      "I ni baara{name} ! Excellent travail ! 🌟",
      "Parfait{name} ! Le Mali est fier de toi ! 🥁",
      "Tu joues avec les mots comme le kora joue ses notes{name} ! 🎵",
      "Excellent{name} ! Tu avances sur la route de Bamako ! 🌍",
      "Super{name} ! Le djembé rythme ta progression ! 🥁",
      "Bien joué{name} ! L'Empire du Mali applaudit ! 🏛️",
      "Magnifique{name} ! Tu parles la langue de Soundiata Keïta ! 🦅",
      "Aw{name} ! Tu maîtrises le bambara pas à pas ! ✨",
    ],
    wrong: [
      "Taa ko{name} ! Continue, ça viendra ! 💪",
      "Pas grave{name} ! Le djembé ne s'apprend pas en un jour !",
      "Courage{name} ! Bamako a été bâtie brique par brique !",
      "Presque{name} ! Le bambara est riche, chaque mot compte ! 🌍",
      "Ne te décourage pas{name} ! Même les sages de Tombouctou ont appris !",
      "Ça arrive{name} ! Le Niger coule lentement mais il arrive à la mer 🌊",
    ],
    streak: [
      "Quelle série{name} ! Le djembé résonne pour toi ! 🥁🔥",
      "Incroyable{name} ! Tu parles bambara comme à Bamako ! 🔥",
      "Fantastique{name} ! L'Empire mandé revit en toi ! 🏛️✨",
      "{name}, tu es en feu ! Les griots mandingues te célèbrent ! 🎵🔥",
    ],
    comeback: [
      "Le fleuve Niger reprend son cours{name} ! 🌊",
      "Belle reprise{name} ! L'Empire Mandé est de retour ! 🦅",
      "{name}, tu rebondis comme Bamako après la saison sèche ! ☀️",
    ],
  },
  'la': {
    correct: [
      "Malamu mingi{name} ! Très bien ! ✨",
      "Ozali malamu{name} ! Tu es excellent ! 🌟",
      "La rumba congolaise danse pour toi{name} ! 🎶",
      "Excellent{name} ! Le Congo-Kinshasa applaudit ! 🏙️",
      "Parfait{name} ! Le fleuve Congo coule avec toi ! 🌊",
      "Tu parles lingala comme à Kin-la-Belle{name} ! 🌆",
      "Magnifique{name} ! Kinshasa t'accueille les bras ouverts ! 🌍",
      "Bien joué{name} ! Les rives du Congo vibrent ! 🌿",
    ],
    wrong: [
      "Pambeli{name} ! En avant, ça viendra ! 💪",
      "Pas grave{name} ! Le Congo est grand, le lingala aussi ! 🌍",
      "Courage{name} ! Même le fleuve Congo fait des détours avant d'arriver !",
      "Presque{name} ! Le lingala est musical, chaque mot chante 🎵",
      "Continue{name} ! Kinshasa ne s'est pas construite en un jour !",
      "Ne lâche pas{name} ! La forêt tropicale cache des trésors, comme ce mot 🌿",
    ],
    streak: [
      "Quelle série{name} ! La rumba congolaise rythme ta victoire ! 🎶🔥",
      "Incroyable{name} ! Tu parles lingala comme à Kin-la-Belle ! 🌆🔥",
      "Fantastique{name} ! Le fleuve Congo n'arrête jamais — ni toi ! 🌊✨",
      "{name}, tu es en feu ! Kinshasa te célèbre ! 🔥🏙️",
    ],
    comeback: [
      "Le Congo reprend son élan{name} ! 🌊",
      "Belle reprise{name} ! Kin-la-Belle retrouve son sourire ! 🌆🚀",
      "{name}, tu rebondis comme la rumba ! 🎶",
    ],
  },
  'ff': {
    correct: [
      "Ko moƴƴi{name} ! C'est excellent ! ✨",
      "A jaaraama{name} ! Bel effort ! 🌟",
      "Parfait{name} ! Le Sahel est fier de son apprenant ! 🌾",
      "Tu avances comme le troupeau peul sur la piste{name} ! 🐄",
      "Excellent{name} ! Les Peuls nomades portent la connaissance partout ! 🗺️",
      "Tu parles la langue des bergers du Sahel{name} ! 🌅",
      "Magnifique{name} ! Le Fouta-Djalon résonne avec ta progression ! ⛰️",
      "Bien joué{name} ! La savane entière t'a entendu ! 🌾",
    ],
    wrong: [
      "Jokku{name} ! Continue, la savane récompense la persévérance ! 💪",
      "Pas grave{name} ! Même les bergers apprennent de leurs troupeaux !",
      "Courage{name} ! Le Sahel se traverse pas à pas 🐫",
      "Presque{name} ! Le peul est une langue nomade, elle demande du voyage !",
      "Continue{name} ! Les Peuls parcourent des milliers de km — tu peux ! 🗺️",
      "Ça arrive{name} ! La pluie revient toujours après la sécheresse au Sahel 🌧️",
    ],
    streak: [
      "Quelle série{name} ! Tu traverses le Sahel sans t'arrêter ! 🐄🔥",
      "Incroyable{name} ! Tu parles peul comme un vrai nomade ! 🗺️🔥",
      "Fantastique{name} ! Le Fouta-Djalon tremble d'admiration ! ⛰️✨",
      "{name}, tu es en feu ! La savane chante pour toi ! 🌾🔥",
    ],
    comeback: [
      "Le troupeau reprend sa route{name} ! 🐄",
      "Belle reprise{name} ! La pluie est revenue sur le Sahel ! 🌧️🚀",
      "{name}, tu rebondis comme le nomade après la tempête ! 🌅",
    ],
  },
  'co': {
    correct: [
      "Mēt anok{name} ! Je suis avec toi — magnifique ! ✨",
      "Šahri{name} ! Excellent travail ! 🌟",
      "Les pharaons t'auraient gravé dans leurs hiéroglyphes{name} ! 📜",
      "Tu parles la langue des premiers chrétiens d'Égypte{name} ! ✝️",
      "Excellent{name} ! Le Nil est témoin de ta progression ! 🌊",
      "Tu ressuscites une langue millénaire{name} ! 🏛️",
      "Magnifique{name} ! Alexandrie applaudit ! 📚",
      "Bien joué{name} ! Les moines du désert seraient admiratifs ! 🏔️",
    ],
    wrong: [
      "Sok{name} ! Essaie encore, la sagesse vient avec le temps ! 💪",
      "Pas grave{name} ! Même Champollion a mis du temps pour les hiéroglyphes !",
      "Courage{name} ! Les pyramides n'ont pas été bâties en un sprint 🏛️",
      "Presque{name} ! Le copte est ancien, chaque mot est un trésor !",
      "Continue{name} ! Le Nil revient toujours après la crue !",
      "Ça arrive{name} ! Même les scribes faisaient des erreurs sur les papyrus 📜",
    ],
    streak: [
      "Quelle série{name} ! Les pharaons gravaient moins vite que tu n'apprends ! 🏛️🔥",
      "Incroyable{name} ! Tu maîtrises le copte comme un moine d'Alexandrie ! 📚🔥",
      "Fantastique{name} ! Le Nil déborde de ta progression ! 🌊✨",
      "{name}, tu es en feu ! L'Égypte millénaire te célèbre ! 🔥📜",
    ],
    comeback: [
      "Le sphinx sourit à nouveau{name} ! 🏛️",
      "Belle reprise{name} ! Le Nil reprend son cours ! 🌊🚀",
      "{name}, tu rebondis comme la crue du Nil ! 🌊",
    ],
  },
  'sw': {
    correct: [
      "Vizuri sana{name} ! Parfaitement bien ! ✨",
      "Hongera{name} ! Félicitations ! 🌟",
      "Le Kilimandjaro s'incline devant toi{name} ! 🏔️",
      "Tu parles la langue de 5 pays d'Afrique de l'Est{name} ! 🌍",
      "Excellent{name} ! Les dhows de la côte swahilie naviguent pour toi ! ⛵",
      "Tu avances comme un safari dans la savane{name} ! 🦁",
      "Magnifique{name} ! Zanzibar résonne avec ta progression ! 🏝️",
      "Bien joué{name} ! Les marchands de l'Océan Indien sont fiers ! ⚓",
    ],
    wrong: [
      "Jaribu tena{name} ! Essaie encore ! 💪",
      "Pas grave{name} ! Même Zanzibar a connu des tempêtes !",
      "Courage{name} ! Le safari commence toujours par le premier pas 🦁",
      "Presque{name} ! Le swahili mélange mille ans de cultures — il est riche !",
      "Continue{name} ! Le Kilimandjaro ne connaît pas le découragement !",
      "Ça arrive{name} ! Les dhows reviennent toujours au port après la tempête ⛵",
    ],
    streak: [
      "Quelle série{name} ! Le Kilimandjaro tremble sous tes pieds ! 🏔️🔥",
      "Incroyable{name} ! Tu parles swahili comme sur la côte de Zanzibar ! 🏝️🔥",
      "Fantastique{name} ! L'Afrique de l'Est entière applaudit ! 🌍✨",
      "{name}, tu es en feu ! Même les lions de la savane te respectent ! 🦁🔥",
    ],
    comeback: [
      "Le dhow reprend la mer{name} ! ⛵",
      "Belle reprise{name} ! Zanzibar te sourit à nouveau ! 🏝️🚀",
      "{name}, tu rebondis comme le safari après la pluie ! 🦁",
    ],
  },
  'ka': {
    correct: [
      "Nefer{name} ! C'est parfait, comme gravé dans la pierre ! ✨",
      "Excellent{name} ! Les scribes du temple t'auraient formé ! 📜",
      "Tu lis les hiéroglyphes comme un prêtre d'Amon{name} ! 𓋹",
      "Magnifique{name} ! Le regard d'Horus veille sur toi ! 𓂀",
      "Bien joué{name} ! Ta maîtrise durera comme les pyramides ! 🔺",
      "Formidable{name} ! Les dieux du Nil sont fiers ! 𓇳",
    ],
    wrong: [
      "Continue{name} ! Même Champollion a tâtonné avant de déchiffrer ! 💪",
      "Pas grave{name} ! Chaque glyphe se mérite avec patience !",
      "Courage{name} ! Les pyramides ne se sont pas bâties en un jour 🔺",
      "Presque{name} ! Ce signe cache encore son secret, retente !",
      "Ça arrive{name} ! Même les scribes raturaient leurs papyrus 📜",
    ],
    streak: [
      "Quelle série{name} ! Tu graves les glyphes plus vite qu'un scribe royal ! 🔥",
      "Incroyable{name} ! Le Nil n'a jamais vu pareille progression ! 𓇳🔥",
      "{name}, tu es en feu ! Les pharaons t'accorderaient un titre ! 👑🔥",
    ],
    comeback: [
      "L'Œil d'Horus s'ouvre à nouveau{name} ! 𓂀",
      "Belle reprise{name} ! Le Nil reprend son cours ! 𓇳🚀",
      "{name}, tu renais comme le phénix Bennou ! 𓅓",
    ],
  },
  'yo': {
    correct: [
      "Ó dára gan-an{name} ! C'est parfait ! ✨",
      "Kú iṣẹ́{name} ! Beau travail ! 🌟",
      "Les tambours parlants d'Ọ̀yọ́ résonnent pour toi{name} ! 🥁",
      "Tu parles la langue de plus de 40 millions de personnes{name} ! 🌍",
      "Excellent{name} ! Ilé-Ifẹ̀, berceau des Yoruba, est fière de toi ! 🏛️",
      "Magnifique{name} ! Ta progression brille comme un tissu adire ! 🎨",
      "Bien joué{name} ! Les marchés de Lagos applaudissent ! 🛍️",
    ],
    wrong: [
      "Gbìyànjú lẹ́ẹ̀kan sí i{name} ! Essaie encore ! 💪",
      "Pas grave{name} ! Les tons du yoruba demandent de l'oreille !",
      "Courage{name} ! La rivière Ọ̀ṣun coule doucement mais sûrement 🌊",
      "Presque{name} ! Écoute bien les tons, ils changent le sens des mots !",
      "Continue{name} ! Même le tambour parlant s'accorde avant de jouer 🥁",
    ],
    streak: [
      "Quelle série{name} ! Le tambour dùndún bat pour toi ! 🥁🔥",
      "Incroyable{name} ! Tu parles yoruba comme à Ìbàdàn ! 🔥",
      "{name}, tu es en feu ! Les rois d'Ọ̀yọ́ t'accorderaient un oríkì ! 👑🔥",
    ],
    comeback: [
      "Le tambour reprend son rythme{name} ! 🥁",
      "Belle reprise{name} ! Ọ̀ṣun te sourit à nouveau ! 🌊🚀",
      "{name}, tu rebondis comme la danse des masques Gẹ̀lẹ̀dé ! 🎭",
    ],
  },
};

const languageEncouragementEN: Record<LanguageCode, EncouragementPool> = {
  'wf': {
    correct: [
      "Waaw{name}! Exactly — you're already speaking Wolof! ✨",
      "Dafa rafet{name}! That's perfect! 🌺",
      "You've got a gift for Wolof{name}! The griots would be proud! 🎵",
      "Teranga is also about sharing knowledge{name} — and you're doing just that! 🤝",
      "You've mastered that like a true Dakar native{name}! 🦁",
      "The Lion of Senegal approves{name}! 🦁",
      "Perfect{name}! The baobab grows slowly, but solidly — just like you! 🌳",
      "Excellent{name}! Even the Lebou of Dakar are applauding! ☀️",
    ],
    wrong: [
      "Bul sañ{name}! Don't get discouraged, Wolof takes time! 💪",
      "No worries{name}! Even Dakar natives had to learn it one day!",
      "The griots say: every mistake is a lesson{name}! 🎵",
      "Stay strong{name}! Teranga is also about welcoming your mistakes! 🌺",
      "Almost{name}! One more try and you'll have the Teranga of this word! 🤝",
      "Keep going{name}! The Sahel isn't crossed in a single step 🌾",
    ],
    streak: [
      "What a streak{name}! The griots would sing for you tonight! 🎵🔥",
      "{name}, you're on fire! Even Dakar will remember this! 🔥🦁",
      "Wolof has no more secrets for you{name}! ✨",
      "Impressive{name}! That's the Teranga of knowledge! 🌟",
    ],
    comeback: [
      "The baobab always stands strong{name}! 🌳",
      "Great comeback{name}! The Lion of Senegal is back! 🦁🚀",
      "{name}, you're bouncing back like Dakar after the rain! ☀️",
    ],
  },
  'ba': {
    correct: [
      "I ni baara{name}! Excellent work! 🌟",
      "Perfect{name}! Mali is proud of you! 🥁",
      "You play with words like the kora plays its notes{name}! 🎵",
      "Excellent{name}! You're moving forward on the road to Bamako! 🌍",
      "Great{name}! The djembe is drumming out your progress! 🥁",
      "Well done{name}! The Mali Empire applauds! 🏛️",
      "Magnificent{name}! You're speaking the language of Soundiata Keïta! 🦅",
      "Aw{name}! You're mastering Bambara step by step! ✨",
    ],
    wrong: [
      "Taa ko{name}! Keep going, it'll come! 💪",
      "No worries{name}! The djembe isn't learned in a day!",
      "Stay strong{name}! Bamako was built brick by brick!",
      "Almost{name}! Bambara is rich, every word counts! 🌍",
      "Don't get discouraged{name}! Even the sages of Timbuktu had to learn!",
      "It happens{name}! The Niger flows slowly but it reaches the sea 🌊",
    ],
    streak: [
      "What a streak{name}! The djembe resounds for you! 🥁🔥",
      "Incredible{name}! You speak Bambara like a true Bamako native! 🔥",
      "Fantastic{name}! The Mandé Empire lives again through you! 🏛️✨",
      "{name}, you're on fire! The Mandinka griots are celebrating you! 🎵🔥",
    ],
    comeback: [
      "The Niger River resumes its course{name}! 🌊",
      "Great comeback{name}! The Mandé Empire is back! 🦅",
      "{name}, you're bouncing back like Bamako after the dry season! ☀️",
    ],
  },
  'la': {
    correct: [
      "Malamu mingi{name}! Very good! ✨",
      "Ozali malamu{name}! You're excellent! 🌟",
      "Congolese rumba is dancing for you{name}! 🎶",
      "Excellent{name}! Congo-Kinshasa applauds! 🏙️",
      "Perfect{name}! The Congo River flows with you! 🌊",
      "You speak Lingala like in Kin-la-Belle{name}! 🌆",
      "Magnificent{name}! Kinshasa welcomes you with open arms! 🌍",
      "Well done{name}! The banks of the Congo are buzzing! 🌿",
    ],
    wrong: [
      "Pambeli{name}! Onward, it'll come! 💪",
      "No worries{name}! The Congo is vast, and so is Lingala! 🌍",
      "Stay strong{name}! Even the Congo River takes detours before arriving!",
      "Almost{name}! Lingala is musical, every word sings 🎵",
      "Keep going{name}! Kinshasa wasn't built in a day!",
      "Don't give up{name}! The rainforest hides treasures, just like this word 🌿",
    ],
    streak: [
      "What a streak{name}! Congolese rumba is drumming out your victory! 🎶🔥",
      "Incredible{name}! You speak Lingala like in Kin-la-Belle! 🌆🔥",
      "Fantastic{name}! The Congo River never stops — and neither do you! 🌊✨",
      "{name}, you're on fire! Kinshasa is celebrating you! 🔥🏙️",
    ],
    comeback: [
      "The Congo regains its momentum{name}! 🌊",
      "Great comeback{name}! Kin-la-Belle finds its smile again! 🌆🚀",
      "{name}, you're bouncing back like the rumba! 🎶",
    ],
  },
  'ff': {
    correct: [
      "Ko moƴƴi{name}! That's excellent! ✨",
      "A jaaraama{name}! Great effort! 🌟",
      "Perfect{name}! The Sahel is proud of its learner! 🌾",
      "You're advancing like the Fulani herd on the trail{name}! 🐄",
      "Excellent{name}! Fulani nomads carry knowledge everywhere! 🗺️",
      "You speak the language of the Sahel's shepherds{name}! 🌅",
      "Magnificent{name}! The Fouta Djallon resounds with your progress! ⛰️",
      "Well done{name}! The whole savanna heard you! 🌾",
    ],
    wrong: [
      "Jokku{name}! Keep going, the savanna rewards perseverance! 💪",
      "No worries{name}! Even shepherds learn from their herds!",
      "Stay strong{name}! The Sahel is crossed step by step 🐫",
      "Almost{name}! Fulani is a nomadic language, it calls for travel!",
      "Keep going{name}! The Fulani travel thousands of km — you can do it! 🗺️",
      "It happens{name}! The rain always returns after the drought in the Sahel 🌧️",
    ],
    streak: [
      "What a streak{name}! You're crossing the Sahel without stopping! 🐄🔥",
      "Incredible{name}! You speak Fulani like a true nomad! 🗺️🔥",
      "Fantastic{name}! The Fouta Djallon trembles with admiration! ⛰️✨",
      "{name}, you're on fire! The savanna sings for you! 🌾🔥",
    ],
    comeback: [
      "The herd resumes its journey{name}! 🐄",
      "Great comeback{name}! The rain has returned to the Sahel! 🌧️🚀",
      "{name}, you're bouncing back like the nomad after the storm! 🌅",
    ],
  },
  'co': {
    correct: [
      "Mēt anok{name}! I am with you — magnificent! ✨",
      "Šahri{name}! Excellent work! 🌟",
      "The pharaohs would have carved you into their hieroglyphs{name}! 📜",
      "You speak the language of Egypt's first Christians{name}! ✝️",
      "Excellent{name}! The Nile witnesses your progress! 🌊",
      "You're reviving a millennia-old language{name}! 🏛️",
      "Magnificent{name}! Alexandria applauds! 📚",
      "Well done{name}! The desert monks would be in awe! 🏔️",
    ],
    wrong: [
      "Sok{name}! Try again, wisdom comes with time! 💪",
      "No worries{name}! Even Champollion took time to crack the hieroglyphs!",
      "Stay strong{name}! The pyramids weren't built in a sprint 🏛️",
      "Almost{name}! Coptic is ancient, every word is a treasure!",
      "Keep going{name}! The Nile always returns after the flood!",
      "It happens{name}! Even scribes made mistakes on their papyrus 📜",
    ],
    streak: [
      "What a streak{name}! The pharaohs carved slower than you learn! 🏛️🔥",
      "Incredible{name}! You master Coptic like a monk of Alexandria! 📚🔥",
      "Fantastic{name}! The Nile overflows with your progress! 🌊✨",
      "{name}, you're on fire! Millennia-old Egypt celebrates you! 🔥📜",
    ],
    comeback: [
      "The sphinx smiles again{name}! 🏛️",
      "Great comeback{name}! The Nile resumes its course! 🌊🚀",
      "{name}, you're bouncing back like the flooding of the Nile! 🌊",
    ],
  },
  'sw': {
    correct: [
      "Vizuri sana{name}! Perfectly well done! ✨",
      "Hongera{name}! Congratulations! 🌟",
      "Kilimanjaro bows before you{name}! 🏔️",
      "You speak the language of 5 East African countries{name}! 🌍",
      "Excellent{name}! The dhows of the Swahili coast are sailing for you! ⛵",
      "You're advancing like a safari across the savanna{name}! 🦁",
      "Magnificent{name}! Zanzibar resounds with your progress! 🏝️",
      "Well done{name}! The Indian Ocean traders are proud! ⚓",
    ],
    wrong: [
      "Jaribu tena{name}! Try again! 💪",
      "No worries{name}! Even Zanzibar has weathered storms!",
      "Stay strong{name}! A safari always starts with the first step 🦁",
      "Almost{name}! Swahili blends a thousand years of cultures — it's rich!",
      "Keep going{name}! Kilimanjaro knows no discouragement!",
      "It happens{name}! The dhows always return to port after the storm ⛵",
    ],
    streak: [
      "What a streak{name}! Kilimanjaro trembles beneath your feet! 🏔️🔥",
      "Incredible{name}! You speak Swahili like on the Zanzibar coast! 🏝️🔥",
      "Fantastic{name}! All of East Africa applauds! 🌍✨",
      "{name}, you're on fire! Even the lions of the savanna respect you! 🦁🔥",
    ],
    comeback: [
      "The dhow sets sail again{name}! ⛵",
      "Great comeback{name}! Zanzibar smiles at you again! 🏝️🚀",
      "{name}, you're bouncing back like a safari after the rain! 🦁",
    ],
  },
  'ka': {
    correct: [
      "Nefer{name}! It's perfect, like carved in stone! ✨",
      "Excellent{name}! The temple scribes would have trained you! 📜",
      "You read hieroglyphs like a priest of Amun{name}! 𓋹",
      "Magnificent{name}! The eye of Horus watches over you! 𓂀",
      "Well done{name}! Your mastery will last like the pyramids! 🔺",
      "Wonderful{name}! The gods of the Nile are proud! 𓇳",
    ],
    wrong: [
      "Keep going{name}! Even Champollion fumbled before deciphering it! 💪",
      "No worries{name}! Every glyph is earned with patience!",
      "Stay strong{name}! The pyramids weren't built in a day 🔺",
      "Almost{name}! This sign still hides its secret, try again!",
      "It happens{name}! Even scribes crossed out mistakes on their papyrus 📜",
    ],
    streak: [
      "What a streak{name}! You're carving glyphs faster than a royal scribe! 🔥",
      "Incredible{name}! The Nile has never seen such progress! 𓇳🔥",
      "{name}, you're on fire! The pharaohs would grant you a title! 👑🔥",
    ],
    comeback: [
      "The Eye of Horus opens once more{name}! 𓂀",
      "Great comeback{name}! The Nile resumes its course! 𓇳🚀",
      "{name}, you're reborn like the Bennu phoenix! 𓅓",
    ],
  },
  'yo': {
    correct: [
      "Ó dára gan-an{name}! That's perfect! ✨",
      "Kú iṣẹ́{name}! Great work! 🌟",
      "The talking drums of Ọ̀yọ́ are beating for you{name}! 🥁",
      "You speak the language of over 40 million people{name}! 🌍",
      "Excellent{name}! Ilé-Ifẹ̀, cradle of the Yoruba, is proud of you! 🏛️",
      "Magnificent{name}! Your progress shines like adire cloth! 🎨",
      "Well done{name}! The markets of Lagos are cheering! 🛍️",
    ],
    wrong: [
      "Gbìyànjú lẹ́ẹ̀kan sí i{name}! Try again! 💪",
      "No worries{name}! Yoruba tones take a good ear!",
      "Keep going{name}! The Ọ̀ṣun river flows gently but surely 🌊",
      "Almost{name}! Listen to the tones, they change the meaning of words!",
      "Keep at it{name}! Even the talking drum gets tuned before it plays 🥁",
    ],
    streak: [
      "What a streak{name}! The dùndún drum is beating for you! 🥁🔥",
      "Incredible{name}! You speak Yoruba like in Ìbàdàn! 🔥",
      "{name}, you're on fire! The kings of Ọ̀yọ́ would grant you an oríkì! 👑🔥",
    ],
    comeback: [
      "The drum picks up its rhythm again{name}! 🥁",
      "Great comeback{name}! Ọ̀ṣun smiles on you again! 🌊🚀",
      "{name}, you're bouncing back like the Gẹ̀lẹ̀dé mask dance! 🎭",
    ],
  },
};

/** Paliers de mots total appris (toutes sessions) */
const totalWordsMilestones: Record<number, string> = {
  1:   "Ton 1er mot en {language} {name} ! L'aventure commence ! 🌱",
  5:   "5 mots en {language} {name} ! Bien parti(e) ! ⭐",
  10:  "10 mots appris {name} ! Tu te souviens de tout ? 🔥",
  25:  "25 mots {name} ! Tu maîtrises déjà les bases du {language} ! 💪",
  50:  "50 mots {name} ! Tu es sur la bonne voie ! 🚀",
  100: "100 mots {name} ! Incroyable, tu parles {language} ! 🏆",
  200: "200 mots en {language} {name} ! Tu es vraiment doué(e) ! 🌟",
  500: "500 mots {name} ! Tu maîtrises le {language} ! 👑",
};

/** Paliers de mots appris dans la session en cours */
const sessionMilestones: Record<number, string> = {
  1:  "1er mot de la session {name} ! C'est parti ! 🎯",
  5:  "5 mots dans cette session {name} ! Excellent rythme ! ⚡",
  10: "10 mots aujourd'hui {name} ! Session très productive ! 🎓",
  20: "20 mots dans cette session {name} ! Tu es en feu ! 🔥",
  30: "30 mots dans cette session {name} ! Marathon d'apprentissage ! 🏃",
};

/** Streaks de connexion quotidienne */
const dailyStreakMilestones: Record<number, string> = {
  2:  "{name}, 2 jours d'affilée ! Tu commences une belle habitude ! 📅",
  3:  "3 jours de suite {name} ! Le rythme s'installe ! 🌟",
  7:  "7 jours sans interruption {name} ! Une semaine entière ! 🗓️",
  14: "14 jours {name} ! Deux semaines de régularité ! 💪",
  30: "30 jours {name} ! Un mois de {language} sans faillir ! 🏆",
  60: "60 jours {name} ! Tu es un(e) vrai(e) passionné(e) de {language} ! 🔥",
  100: "100 jours {name} ! Tu es une légende vivante ! 👑",
};

/** Version anglaise des paliers de mots total appris (toutes sessions) */
const totalWordsMilestonesEN: Record<number, string> = {
  1:   "Your 1st word in {language} {name}! The adventure begins! 🌱",
  5:   "5 words in {language} {name}! Off to a great start! ⭐",
  10:  "10 words learned {name}! Do you remember them all? 🔥",
  25:  "25 words {name}! You already master the basics of {language}! 💪",
  50:  "50 words {name}! You're on the right track! 🚀",
  100: "100 words {name}! Incredible, you speak {language}! 🏆",
  200: "200 words in {language} {name}! You're truly gifted! 🌟",
  500: "500 words {name}! You've mastered {language}! 👑",
};

/** Version anglaise des paliers de mots appris dans la session en cours */
const sessionMilestonesEN: Record<number, string> = {
  1:  "1st word of the session {name}! Here we go! 🎯",
  5:  "5 words in this session {name}! Excellent pace! ⚡",
  10: "10 words today {name}! A very productive session! 🎓",
  20: "20 words in this session {name}! You're on fire! 🔥",
  30: "30 words in this session {name}! A true learning marathon! 🏃",
};

/** Version anglaise des streaks de connexion quotidienne */
const dailyStreakMilestonesEN: Record<number, string> = {
  2:  "{name}, 2 days in a row! You're building a great habit! 📅",
  3:  "3 days in a row {name}! The rhythm is setting in! 🌟",
  7:  "7 days without a break {name}! A whole week! 🗓️",
  14: "14 days {name}! Two weeks of consistency! 💪",
  30: "30 days {name}! A whole month of {language} without missing a beat! 🏆",
  60: "60 days {name}! You're a true {language} enthusiast! 🔥",
  100: "100 days {name}! You're a living legend! 👑",
};

type GreetingKey = 'hello' | 'welcome' | 'goodJob' | 'continue';

const languageGreetings: Record<LanguageCode, Record<GreetingKey, string>> = {
  'wf': {
    hello:    "Salamalekoum {name} !",
    welcome:  "Dalal ak jamm {name} !",
    goodJob:  "Rafetna {name} !",
    continue: "Kontine {name} !",
  },
  'ba': {
    hello:    "I ni cé {name} !",
    welcome:  "Bisimila {name} !",
    goodJob:  "I ye baara kè {name} !",
    continue: "Taa {name} !",
  },
  'la': {
    hello:    "Mbote {name} !",
    welcome:  "Boyei malamu {name} !",
    goodJob:  "Malamu mingi {name} !",
    continue: "Bokoba {name} !",
  },
  'ff': {
    hello:    "Jam waali {name} !",
    welcome:  "A jaraama {name} !",
    goodJob:  "Ko moƴƴi {name} !",
    continue: "Jokku {name} !",
  },
  'co': {
    hello:    "Shahri {name} !",
    welcome:  "Kalos irthes {name} !",
    goodJob:  "Mēt anok {name} !",
    continue: "Sok {name} !",
  },
  'sw': {
    hello:    "Habari {name} !",
    welcome:  "Karibu {name} !",
    goodJob:  "Vizuri sana {name} !",
    continue: "Endelea {name} !",
  },
  'ka': {
    hello:    "Iiu {name} !",
    welcome:  "Iiu em hotep {name} !",
    goodJob:  "Nefer {name} !",
    continue: "Iri {name} !",
  },
  'yo': {
    hello:    "Báwo ni {name} !",
    welcome:  "Ẹ káàbọ̀ {name} !",
    goodJob:  "Ó dára gan-an {name} !",
    continue: "Tẹ̀síwájú {name} !",
  }
};

// Les salutations ci-dessous sont des interjections dans la langue cible
// (wolof, bambara, lingala, peul, copte, swahili, kama, yoruba) et non du français :
// elles restent donc identiques en version anglaise, conformément aux règles
// de traduction du composant (on ne traduit jamais les mots de la langue cible).
const languageGreetingsEN: Record<LanguageCode, Record<GreetingKey, string>> = {
  'wf': {
    hello:    "Salamalekoum {name} !",
    welcome:  "Dalal ak jamm {name} !",
    goodJob:  "Rafetna {name} !",
    continue: "Kontine {name} !",
  },
  'ba': {
    hello:    "I ni cé {name} !",
    welcome:  "Bisimila {name} !",
    goodJob:  "I ye baara kè {name} !",
    continue: "Taa {name} !",
  },
  'la': {
    hello:    "Mbote {name} !",
    welcome:  "Boyei malamu {name} !",
    goodJob:  "Malamu mingi {name} !",
    continue: "Bokoba {name} !",
  },
  'ff': {
    hello:    "Jam waali {name} !",
    welcome:  "A jaraama {name} !",
    goodJob:  "Ko moƴƴi {name} !",
    continue: "Jokku {name} !",
  },
  'co': {
    hello:    "Shahri {name} !",
    welcome:  "Kalos irthes {name} !",
    goodJob:  "Mēt anok {name} !",
    continue: "Sok {name} !",
  },
  'sw': {
    hello:    "Habari {name} !",
    welcome:  "Karibu {name} !",
    goodJob:  "Vizuri sana {name} !",
    continue: "Endelea {name} !",
  },
  'ka': {
    hello:    "Iiu {name} !",
    welcome:  "Iiu em hotep {name} !",
    goodJob:  "Nefer {name} !",
    continue: "Iri {name} !",
  },
  'yo': {
    hello:    "Báwo ni {name} !",
    welcome:  "Ẹ káàbọ̀ {name} !",
    goodJob:  "Ó dára gan-an {name} !",
    continue: "Tẹ̀síwájú {name} !",
  }
};

const languageNames: Record<LanguageCode, string> = {
  'wf': 'wolof',
  'ba': 'bambara',
  'la': 'lingala',
  'ff': 'peul',
  'co': 'copte',
  'sw': 'swahili',
  'ka': 'kama',
  'yo': 'yoruba'
};

const categoryExplanations: Record<AppMode, string[]> = {
  menu: [
    "Choisis une activité pour apprendre !",
    "Que veux-tu faire aujourd'hui {name} ?",
    "Sélectionne un mode de jeu pour commencer {name}."
  ],
  learn: [
    "Apprends de nouveaux mots et mémorise-les {name}.",
    "Découvre du vocabulaire utile en {language}.",
    "Enrichis ton vocabulaire {name} !"
  ],
  review: [
    "Révise les mots que tu as déjà appris {name}.",
    "La révision aide à mémoriser sur le long terme.",
    "Vérifie ce dont tu te souviens en {language} !"
  ],
  quiz: [
    "Teste tes connaissances en t'amusant {name} !",
    "Mets ton vocabulaire {language} à l'épreuve.",
    "Réponds aux questions pour gagner des points {name}."
  ],
  sentenceConstruction: [
    "Construis des phrases complètes en {language}.",
    "Mets les mots dans le bon ordre {name}.",
    "Apprends à former des phrases correctes en {language}."
  ],
  sentenceGap: [
    "Complète les phrases avec le bon mot {name}.",
    "Trouve le mot manquant dans chaque phrase en {language}.",
    "Entraîne-toi à comprendre les phrases complètes {name}."
  ],
  grammar: [
    "Apprends les règles de grammaire essentielles du {language}.",
    "Comprends comment la langue {language} fonctionne {name}.",
    "Maîtrise la structure de la langue {name}."
  ],
  exercise: [
    "Entraîne-toi avec différents types d'exercices {name}.",
    "Pratique tes compétences en {language}.",
    "Consolide ton apprentissage par la pratique {name}."
  ],
  progression: [
    "Consulte tes statistiques d'apprentissage {name} !",
    "Visualise ta progression et tes succès en {language}.",
    "Analyse ton parcours et tes accomplissements {name}."
  ],
  profile: [
    "Personnalise ton profil et retrouve tes amis {name} !",
    "Ajoute des amis et compare vos progressions.",
    "Ton profil, ton identité d'apprenante {name}."
  ],
  challenge: [
    "Défi en cours ! Montre ce que tu sais {name} !",
    "À toi de jouer ! Bats ton adversaire au quiz de {language}.",
    "Mode Défi : qui est le meilleur en {language} ?"
  ],
  minigames: [
    "Mini-jeux culturels ! Teste tes connaissances sur {language}.",
    "Histoire, politique, géographie... À toi de jouer {name} !",
    "Découvre la culture autour de la langue {language} !"
  ],
  dialogue: [
    "Conversations interactives en {language} !",
    "Réponds correctement pour avancer dans le dialogue, {name} !",
    "Entraîne-toi à parler {language} en contexte réel !"
  ],
  songAnalyzer: [
    "Analyse les paroles et découvre la grammaire en musique, {name} !",
    "Clique sur un mot pour voir sa traduction et sa règle grammaticale !",
    "Apprends le {language} à travers les chansons !"
  ],
  wordle: [
    "Trouve le mot du jour en {language}, {name} !",
    "Un nouveau mot à deviner chaque jour !",
    "Devine le mot en 6 essais maximum {name} !"
  ],
  memoryDuel: [
    "Défie un ami au Memory en {language}, {name} !",
    "Retrouve les paires de mots avant ton adversaire !",
    "Trouve une paire et rejoue {name} !"
  ],
  forge: [
    "Les lettres sortent du feu : forge les mots en {language}, {name} !",
    "Attention aux lettres intruses {name} !",
    "Pose les lettres sur l'enclume, dans l'ordre !"
  ],
  weaver: [
    "Tisse les phrases en {language}, fil après fil {name} !",
    "Choisis les mots dans le bon ordre !",
    "Attention aux bobines intruses {name} !"
  ]
};

const categoryExplanationsEN: Record<AppMode, string[]> = {
  menu: [
    "Choose an activity to start learning!",
    "What do you want to do today {name}?",
    "Select a game mode to get started {name}."
  ],
  learn: [
    "Learn new words and memorize them {name}.",
    "Discover useful vocabulary in {language}.",
    "Build up your vocabulary {name}!"
  ],
  review: [
    "Review the words you've already learned {name}.",
    "Reviewing helps you remember for the long term.",
    "Check how much {language} you remember!"
  ],
  quiz: [
    "Test your knowledge while having fun {name}!",
    "Put your {language} vocabulary to the test.",
    "Answer the questions to earn points {name}."
  ],
  sentenceConstruction: [
    "Build complete sentences in {language}.",
    "Put the words in the right order {name}.",
    "Learn to form correct sentences in {language}."
  ],
  sentenceGap: [
    "Complete the sentences with the right word {name}.",
    "Find the missing word in each {language} sentence.",
    "Practice understanding full sentences {name}."
  ],
  grammar: [
    "Learn the essential grammar rules of {language}.",
    "Understand how the {language} language works {name}.",
    "Master the structure of the language {name}."
  ],
  exercise: [
    "Practice with different types of exercises {name}.",
    "Practice your {language} skills.",
    "Reinforce your learning through practice {name}."
  ],
  progression: [
    "Check out your learning stats {name}!",
    "See your progress and achievements in {language}.",
    "Review your journey and accomplishments {name}."
  ],
  profile: [
    "Customize your profile and find your friends {name}!",
    "Add friends and compare your progress.",
    "Your profile, your learner identity {name}."
  ],
  challenge: [
    "Challenge underway! Show what you know {name}!",
    "Your turn! Beat your opponent in the {language} quiz.",
    "Challenge Mode: who's the best at {language}?"
  ],
  minigames: [
    "Cultural mini-games! Test your knowledge of {language}.",
    "History, politics, geography... Your turn to play {name}!",
    "Discover the culture behind the {language} language!"
  ],
  dialogue: [
    "Interactive conversations in {language}!",
    "Answer correctly to move forward in the dialogue, {name}!",
    "Practice speaking {language} in real-life context!"
  ],
  songAnalyzer: [
    "Analyze the lyrics and discover grammar through music, {name}!",
    "Click on a word to see its translation and grammar rule!",
    "Learn {language} through songs!"
  ],
  wordle: [
    "Find today's word in {language}, {name}!",
    "A new word to guess every day!",
    "Guess the word in 6 tries or less {name}!"
  ],
  memoryDuel: [
    "Challenge a friend to Memory in {language}, {name}!",
    "Find the matching pairs before your opponent!",
    "Find a pair and play again {name}!"
  ],
  forge: [
    "Letters come out of the fire: forge the words in {language}, {name}!",
    "Watch out for the extra letters {name}!",
    "Place the letters on the anvil, in order!"
  ],
  weaver: [
    "Weave the sentences in {language}, thread by thread {name}!",
    "Pick the words in the right order!",
    "Watch out for the extra spools {name}!"
  ]
};

// ===================================================================
// Styled components
// ===================================================================

const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`;

const streakCelebration = keyframes`
  0%   { transform: translateY(0)    rotate(0deg)   scale(1); }
  15%  { transform: translateY(-22px) rotate(-12deg) scale(1.15); }
  30%  { transform: translateY(0)    rotate(10deg)  scale(1); }
  45%  { transform: translateY(-18px) rotate(-8deg)  scale(1.12); }
  60%  { transform: translateY(0)    rotate(6deg)   scale(1); }
  75%  { transform: translateY(-10px) rotate(-4deg)  scale(1.08); }
  100% { transform: translateY(0)    rotate(0deg)   scale(1); }
`;

// Note: position:fixed est relatif au viewport car le composant est rendu
// via un React Portal directement dans document.body (contourne le
// stacking context créé par framer-motion sur la motion.div parente).
const MascotContainer = styled.div`
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  cursor: pointer;
  transition: all 0.3s ease;
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }

  &:hover {
    filter: drop-shadow(0 0 10px rgba(0,0,0,0.3));
  }

  @media (min-width: 1024px) {
    top: auto;
    bottom: 120px;
    left: auto;
    right: 30px;
    transform: none;
    scale: 0.85;
  }

  @media (max-width: 768px) {
    top: 70px;
    left: auto;
    right: 15px;
    transform: none;
    scale: 0.8;
  }
`;

const SpeechBubbleWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: row-reverse;
  }
`;

interface SpeechBubbleProps {
  $isDarkTheme: boolean;
}

const SpeechBubble = styled(motion.div)<SpeechBubbleProps>`
  position: relative;
  background-color: ${props => props.$isDarkTheme ? '#1e293b' : 'white'};
  color: ${props => props.$isDarkTheme ? '#f8fafc' : '#333'};
  border: 2px solid ${props => props.$isDarkTheme ? '#475569' : '#333'};
  border-radius: 10px;
  padding: 10px 15px;
  max-width: 220px;
  font-size: 14px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  line-height: 1.4;
  margin: 0 10px;

  &::before {
    content: '';
    position: absolute;
    left: -10px;
    top: 20px;
    border-right: 10px solid ${props => props.$isDarkTheme ? '#475569' : '#333'};
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
  }

  &::after {
    content: '';
    position: absolute;
    left: -7px;
    top: 22px;
    border-right: 8px solid ${props => props.$isDarkTheme ? '#1e293b' : 'white'};
    border-top: 8px solid transparent;
    border-bottom: 8px solid transparent;
  }

  @media (max-width: 768px) {
    max-width: 160px;
    font-size: 12px;

    &::before, &::after {
      left: auto;
      right: -10px;
      border-right: none;
      border-left: 10px solid ${props => props.$isDarkTheme ? '#475569' : '#333'};
      top: 20px;
    }
    &::after {
      right: -7px;
      border-left: 8px solid ${props => props.$isDarkTheme ? '#1e293b' : 'white'};
      border-right: none;
    }
  }
`;

const MascotImage = styled(motion.img)<{ $celebrating?: boolean }>`
  width: 80px;
  height: 80px;
  object-fit: contain;
  animation: ${({ $celebrating }) => $celebrating
    ? css`${streakCelebration} 0.7s ease-in-out 3`
    : css`${bounce} 2s ease-in-out infinite`};

  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
  }
`;

// ===================================================================
// Composant principal
// ===================================================================

const InteractiveMascot: React.FC<MascotProps> = ({
  onClick,
  currentCategory,
  streak = 0,
  lastAnswerCorrect,
  languageCode = 'wf',
  userName,
  wordsLearned = 0,
  sessionWordsLearned = 0,
}) => {
  const { theme } = useTheme();
  const isDarkTheme = theme === 'dark';

  const { i18n } = useTranslation();
  const isEnglish = (i18n.language || '').toLowerCase().startsWith('en');

  const [message, setMessage]       = useState<string>('');
  const [showMessage, setShowMessage] = useState<boolean>(false);

  const previousCategory  = useRef<AppMode | null>(null);
  const lastActivity      = useRef<number>(Date.now());
  const wasCorrect        = useRef<boolean | null>(null);
  const greetingShown     = useRef<boolean>(false);
  const useNameCounter    = useRef(0);
  const MAX_NAME_USES     = 3;

  // Refs pour détecter les franchissements de paliers (pas le rendu initial)
  const prevWordsLearned  = useRef<number | null>(null);
  const prevSessionWords  = useRef<number | null>(null);

  // Streak lu au montage (sans incrémenter) — mis à jour quand des mots sont appris
  const dailyStreak    = useRef<number>(readDailyStreak());
  const streakMsgShown = useRef<boolean>(false);

  // Animation streak journalier
  const [showDailyStreakAnim, setShowDailyStreakAnim] = useState(false);
  const [dailyStreakAnimCount, setDailyStreakAnimCount] = useState(0);
  const [isCelebrating, setIsCelebrating] = useState(false);

  // Animation mot appris
  const [showWordLearnedAnim, setShowWordLearnedAnim] = useState(false);

  // Anecdotes culturelles (A) — index de la dernière affichée pour éviter les répétitions
  const lastAnecdoteIndex = useRef<number>(-1);

  // Phrase du jour (D)
  const phraseShownRef = useRef<boolean>(false);

  // ----------------------------------------------------------------
  // Utilitaires
  // ----------------------------------------------------------------

  const extractUserName = useCallback(() => {
    if (!userName) return '';
    if (typeof userName === 'string') return userName;
    if (typeof userName === 'object') {
      const u: any = userName;
      return u.given_name || u.firstName || u.name || u.displayName ||
             (u.email && u.email.split('@')[0]) || '';
    }
    return '';
  }, [userName]);

  const formatMessage = useCallback((msg: string) => {
    const languageName = languageNames[languageCode] || 'wolof';
    const name = extractUserName();
    let includeName = false;
    if (name) {
      if (useNameCounter.current < MAX_NAME_USES) {
        includeName = true;
        useNameCounter.current++;
      } else {
        includeName = Math.random() < 0.2;
      }
    }
    return msg
      .replace(/\{name\}/g,     includeName ? ` ${name}` : '')
      .replace(/\{language\}/g, languageName);
  }, [languageCode, extractUserName]);

  const showTimedMessage = useCallback((msg: string, durationMs: number = 4000) => {
    setMessage(msg);
    setShowMessage(true);
    const timer = setTimeout(() => setShowMessage(false), durationMs);
    return timer;
  }, []);

  const getLanguageGreeting = useCallback(() => {
    const greetings = (isEnglish && languageGreetingsEN[languageCode]) ||
                       languageGreetings[languageCode] ||
                       languageGreetings['wf'];
    const values = Object.values(greetings);
    const raw = values[Math.floor(Math.random() * values.length)];
    const name = extractUserName();
    return raw.replace(/\{name\}/g, (name && Math.random() < 0.25) ? ` ${name}` : '');
  }, [languageCode, extractUserName, isEnglish]);

  const getMessage = useCallback(() => {
    const hasChangedGame = previousCategory.current !== null &&
                           previousCategory.current !== currentCategory;
    lastActivity.current = Date.now();

    if (!greetingShown.current || hasChangedGame) {
      greetingShown.current = true;
      if (Math.random() < 0.3) return getLanguageGreeting();
      const exps = (isEnglish && categoryExplanationsEN[currentCategory]) ||
                   categoryExplanations[currentCategory] || [];
      if (exps.length) return formatMessage(exps[Math.floor(Math.random() * exps.length)]);
    }

    const langMsgs = (isEnglish && languageEncouragementEN[languageCode]) ||
                      languageEncouragement[languageCode] ||
                      languageEncouragement['wf'];

    if (lastAnswerCorrect === false) {
      wasCorrect.current = false;
      const msgs = langMsgs.wrong;
      return formatMessage(msgs[Math.floor(Math.random() * msgs.length)]);
    }
    if (lastAnswerCorrect === true && wasCorrect.current === false) {
      wasCorrect.current = true;
      const msgs = langMsgs.comeback;
      return formatMessage(msgs[Math.floor(Math.random() * msgs.length)]);
    }
    if (streak >= 5) {
      const msgs = langMsgs.streak;
      return formatMessage(msgs[Math.floor(Math.random() * msgs.length)]);
    }
    if (lastAnswerCorrect === true) {
      const msgs = langMsgs.correct;
      return formatMessage(msgs[Math.floor(Math.random() * msgs.length)]);
    }

    const exps = (isEnglish && categoryExplanationsEN[currentCategory]) ||
                 categoryExplanations[currentCategory] || [];
    if (exps.length) return formatMessage(exps[Math.floor(Math.random() * exps.length)]);

    return '';
  }, [streak, lastAnswerCorrect, currentCategory, formatMessage, getLanguageGreeting, languageCode, isEnglish]);

  // ----------------------------------------------------------------
  // A — Anecdote culturelle au clic
  // ----------------------------------------------------------------
  const showCulturalAnecdote = useCallback(() => {
    const pool = culturalAnecdotes[languageCode] ?? [];
    if (!pool.length) return;
    let idx: number;
    do { idx = Math.floor(Math.random() * pool.length); }
    while (pool.length > 1 && idx === lastAnecdoteIndex.current);
    lastAnecdoteIndex.current = idx;
    showTimedMessage(pool[idx], 6000);
  }, [languageCode, showTimedMessage]);

  // ----------------------------------------------------------------
  // D — Phrase utile du jour
  // ----------------------------------------------------------------
  const showDailyPhrase = useCallback(() => {
    const today = new Date().toISOString().split('T')[0];
    const storageKey = `jogolinga-phrase-shown-${languageCode}-${today}`;
    if (localStorage.getItem(storageKey)) return; // déjà montrée aujourd'hui
    localStorage.setItem(storageKey, '1');
    const dp = getTodayPhrase(languageCode);
    const roman = dp.romanization ? ` [${dp.romanization}]` : '';
    showTimedMessage(`💬 Phrase du jour : « ${dp.phrase} »${roman} = ${dp.translation}`, 7000);
  }, [languageCode, showTimedMessage]);

  // ----------------------------------------------------------------
  // Effets
  // ----------------------------------------------------------------

  // Streak quotidien — affiché une seule fois au montage si > 1
  useEffect(() => {
    if (streakMsgShown.current) return;
    streakMsgShown.current = true;

    const ds = dailyStreak.current;
    const milestones = Object.keys(dailyStreakMilestones).map(Number).sort((a, b) => a - b);
    // Afficher le palier exact atteint aujourd'hui (ou le dernier palier dépassé)
    const reached = [...milestones].reverse().find(m => ds >= m);
    if (reached && ds > 1) {
      // Petit délai pour laisser l'app se charger
      const timer = setTimeout(() => {
        const text = (isEnglish && dailyStreakMilestonesEN[reached]) || dailyStreakMilestones[reached];
        const msg = formatMessage(text);
        showTimedMessage(msg, 5000);
      }, 1500);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionnellement vide : s'exécute une seule fois au montage

  // Changement de catégorie / mode
  useEffect(() => {
    if (previousCategory.current !== currentCategory) {
      if (Math.random() < 0.3) useNameCounter.current = 0;
      const msg = getMessage();
      if (msg) {
        const timer = showTimedMessage(msg, 4000);
        previousCategory.current = currentCategory;
        return () => clearTimeout(timer);
      }
      previousCategory.current = currentCategory;
    }
  }, [currentCategory, getMessage, showTimedMessage]);

  // Réponses (correct / incorrect) et streaks de quiz
  useEffect(() => {
    if (lastAnswerCorrect !== null) {
      const msg = getMessage();
      if (msg) {
        const duration = (streak >= 5 || lastAnswerCorrect === false) ? 3500 : 3000;
        const timer = showTimedMessage(msg, duration);
        return () => clearTimeout(timer);
      }
    }
  }, [streak, lastAnswerCorrect, getMessage, showTimedMessage]);

  // Paliers de mots total appris
  useEffect(() => {
    if (prevWordsLearned.current === null) {
      prevWordsLearned.current = wordsLearned;
      return;
    }
    const prev = prevWordsLearned.current;
    prevWordsLearned.current = wordsLearned;
    const milestones = Object.keys(totalWordsMilestones).map(Number).sort((a, b) => a - b);
    const reached = milestones.find(m => prev < m && wordsLearned >= m);
    if (reached) {
      const text = (isEnglish && totalWordsMilestonesEN[reached]) || totalWordsMilestones[reached];
      const timer = showTimedMessage(formatMessage(text), 5000);
      return () => clearTimeout(timer);
    }
  }, [wordsLearned, formatMessage, showTimedMessage, isEnglish]);

  // Paliers de mots appris dans la session
  useEffect(() => {
    if (prevSessionWords.current === null) {
      prevSessionWords.current = sessionWordsLearned;
      return;
    }
    const prev = prevSessionWords.current;
    prevSessionWords.current = sessionWordsLearned;
    if (sessionWordsLearned <= prev) return; // reset de session, ne pas réagir
    const milestones = Object.keys(sessionMilestones).map(Number).sort((a, b) => a - b);
    const reached = milestones.find(m => prev < m && sessionWordsLearned >= m);
    if (reached) {
      const text = (isEnglish && sessionMilestonesEN[reached]) || sessionMilestones[reached];
      const timer = showTimedMessage(formatMessage(text), 4500);
      return () => clearTimeout(timer);
    }
  }, [sessionWordsLearned, formatMessage, showTimedMessage, isEnglish]);

  // Animation "Mot appris" à chaque nouveau mot appris dans la session
  useEffect(() => {
    if (prevWordsLearned.current === null) return;
    const prev = prevWordsLearned.current as number;
    if (sessionWordsLearned > prev) {
      prevWordsLearned.current = sessionWordsLearned;
      setShowWordLearnedAnim(true);
    } else if (sessionWordsLearned === 0) {
      prevWordsLearned.current = 0;
    }
  }, [sessionWordsLearned]);

  // Conseil aléatoire toutes les 25 secondes (hors menu)
  useEffect(() => {
    if (!showMessage && currentCategory !== 'menu') {
      const interval = setInterval(() => {
        if (!showMessage && Math.random() < 0.15) {
          const msg = getMessage();
          if (msg) showTimedMessage(msg, 3500);
        }
      }, 25000);
      return () => clearInterval(interval);
    }
  }, [showMessage, currentCategory, getMessage, showTimedMessage]);

  // Message de bienvenue personnalisé à la (re)connexion
  useEffect(() => {
    const name = extractUserName();
    if (name) {
      useNameCounter.current++;
      const welcomeBackMsg = isEnglish ? `Great to see you again ${name}!` : `Ravi de te revoir ${name} !`;
      const timer = showTimedMessage(welcomeBackMsg, 3500);
      return () => clearTimeout(timer);
    }
  }, [userName, extractUserName, showTimedMessage, isEnglish]);

  // D — Phrase du jour : affichée une fois après le message de bienvenue
  useEffect(() => {
    if (phraseShownRef.current) return;
    phraseShownRef.current = true;
    const timer = setTimeout(showDailyPhrase, 6000); // délai pour ne pas chevaucher le bienvenue
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionnellement vide : s'exécute une seule fois au montage

  // ----------------------------------------------------------------
  // Rendu — via React Portal pour contourner le stacking context
  // de la motion.div parente (framer-motion v4 applique will-change:transform)
  // ----------------------------------------------------------------

  const handleMascotClick = useCallback(() => {
    if (onClick) onClick();
    showCulturalAnecdote();
  }, [onClick, showCulturalAnecdote]);

  const mascotNode = (
    <MascotContainer data-floating-ibis onClick={handleMascotClick}>
      <SpeechBubbleWrapper>
        <MascotImage
          src="/Mascotte/mascotte5.png"
          alt="Mascotte"
          $celebrating={isCelebrating}
          animate={{ scale: showMessage && !isCelebrating ? [1, 1.1, 1] : 1 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        />

        <AnimatePresence>
          {showMessage && (
            <SpeechBubble
              $isDarkTheme={isDarkTheme}
              initial={{ opacity: 0, scale: 0.8, x: 20 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              exit={{ opacity: 0, scale: 0.8, x: 20 }}
              transition={{ duration: 0.3 }}
            >
              <EmojiText text={message} />
            </SpeechBubble>
          )}
        </AnimatePresence>
      </SpeechBubbleWrapper>
    </MascotContainer>
  );

  return (
    <>
      <DailyStreakAnimation
        show={showDailyStreakAnim}
        streakCount={dailyStreakAnimCount}
        onClose={() => setShowDailyStreakAnim(false)}
      />
      <StreakAnimation
        show={showWordLearnedAnim}
        type="word-learned"
        onComplete={() => setShowWordLearnedAnim(false)}
      />
      {ReactDOM.createPortal(mascotNode, document.body)}
    </>
  );
};

export default InteractiveMascot;
